import React, { useState, useEffect } from 'react';
import { CreditCard, Receipt, Tag, Save, CheckCircle, AlertCircle, Package, Trash2, X } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const StoreSettings = () => {
  const [activeTab, setActiveTab] = useState('payments');
  const [message, setMessage] = useState({ text: '', type: '' });
  const [saving, setSaving] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [couponForm, setCouponForm] = useState({
    code: '',
    discountType: 'percentage',
    discountValue: 0,
    minOrderAmount: 0,
    startDate: '',
    endDate: '',
    enabled: true
  });
  const [settings, setSettings] = useState({
    payments: {
      enabled: false,
      selectedPaymentMethod: ''
    },
    tax: {
      enabled: false,
      taxType: 'state', // 'state' or 'custom'
      selectedState: '',
      taxRate: 0,
      customTaxRate: 0
    },
    coupons: {
      enabled: false
    },
    orders: {
      orderPrefix: 'ORD',
      orderStartNumber: 1000,
      publicAccess: true
    }
  });

  // Reset orders modal state
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetting, setResetting] = useState(false);

  const token = localStorage.getItem('token');

  // US States with tax rates
  const usStates = [
    { code: 'AL', name: 'Alabama', taxRate: 4.0 },
    { code: 'AK', name: 'Alaska', taxRate: 0.0 },
    { code: 'AZ', name: 'Arizona', taxRate: 5.6 },
    { code: 'AR', name: 'Arkansas', taxRate: 6.5 },
    { code: 'CA', name: 'California', taxRate: 7.25 },
    { code: 'CO', name: 'Colorado', taxRate: 2.9 },
    { code: 'CT', name: 'Connecticut', taxRate: 6.35 },
    { code: 'DE', name: 'Delaware', taxRate: 0.0 },
    { code: 'FL', name: 'Florida', taxRate: 6.0 },
    { code: 'GA', name: 'Georgia', taxRate: 4.0 },
    { code: 'HI', name: 'Hawaii', taxRate: 4.0 },
    { code: 'ID', name: 'Idaho', taxRate: 6.0 },
    { code: 'IL', name: 'Illinois', taxRate: 6.25 },
    { code: 'IN', name: 'Indiana', taxRate: 7.0 },
    { code: 'IA', name: 'Iowa', taxRate: 6.0 },
    { code: 'KS', name: 'Kansas', taxRate: 6.5 },
    { code: 'KY', name: 'Kentucky', taxRate: 6.0 },
    { code: 'LA', name: 'Louisiana', taxRate: 4.45 },
    { code: 'ME', name: 'Maine', taxRate: 5.5 },
    { code: 'MD', name: 'Maryland', taxRate: 6.0 },
    { code: 'MA', name: 'Massachusetts', taxRate: 6.25 },
    { code: 'MI', name: 'Michigan', taxRate: 6.0 },
    { code: 'MN', name: 'Minnesota', taxRate: 6.875 },
    { code: 'MS', name: 'Mississippi', taxRate: 7.0 },
    { code: 'MO', name: 'Missouri', taxRate: 4.225 },
    { code: 'MT', name: 'Montana', taxRate: 0.0 },
    { code: 'NE', name: 'Nebraska', taxRate: 5.5 },
    { code: 'NV', name: 'Nevada', taxRate: 6.85 },
    { code: 'NH', name: 'New Hampshire', taxRate: 0.0 },
    { code: 'NJ', name: 'New Jersey', taxRate: 6.625 },
    { code: 'NM', name: 'New Mexico', taxRate: 5.125 },
    { code: 'NY', name: 'New York', taxRate: 4.0 },
    { code: 'NC', name: 'North Carolina', taxRate: 4.75 },
    { code: 'ND', name: 'North Dakota', taxRate: 5.0 },
    { code: 'OH', name: 'Ohio', taxRate: 5.75 },
    { code: 'OK', name: 'Oklahoma', taxRate: 4.5 },
    { code: 'OR', name: 'Oregon', taxRate: 0.0 },
    { code: 'PA', name: 'Pennsylvania', taxRate: 6.0 },
    { code: 'RI', name: 'Rhode Island', taxRate: 7.0 },
    { code: 'SC', name: 'South Carolina', taxRate: 6.0 },
    { code: 'SD', name: 'South Dakota', taxRate: 4.5 },
    { code: 'TN', name: 'Tennessee', taxRate: 7.0 },
    { code: 'TX', name: 'Texas', taxRate: 6.25 },
    { code: 'UT', name: 'Utah', taxRate: 6.1 },
    { code: 'VT', name: 'Vermont', taxRate: 6.0 },
    { code: 'VA', name: 'Virginia', taxRate: 5.3 },
    { code: 'WA', name: 'Washington', taxRate: 6.5 },
    { code: 'WV', name: 'West Virginia', taxRate: 6.0 },
    { code: 'WI', name: 'Wisconsin', taxRate: 5.0 },
    { code: 'WY', name: 'Wyoming', taxRate: 4.0 },
    { code: 'DC', name: 'Washington DC', taxRate: 6.0 }
  ];

  const tabs = [
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'tax', label: 'Tax', icon: Receipt },
    { id: 'coupons', label: 'Coupons', icon: Tag },
    { id: 'orders', label: 'Order Settings', icon: Package }
  ];

  useEffect(() => {
    fetchSettings();
    fetchPaymentMethods();
    fetchCoupons();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/payment-gateways`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data && response.data.success) {
        // Filter enabled payment gateways
        const active = response.data.gateways.filter(gw => gw.enabled && gw.is_verified);
        setPaymentMethods(active);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/store-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data) {
        // Merge fetched settings with defaults to ensure all properties exist
        setSettings(prevSettings => ({
          ...prevSettings,
          ...response.data,
          orders: {
            ...prevSettings.orders,
            ...(response.data.orders || {})
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchCoupons = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/coupons`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCoupons(response.data || []);
    } catch (error) {
      console.error('Error fetching coupons:', error);
    }
  };

  const handleAddCoupon = () => {
    setCouponForm({
      code: '',
      discountType: 'percentage',
      discountValue: 0,
      minOrderAmount: 0,
      startDate: '',
      endDate: '',
      enabled: true
    });
    setEditingCoupon(null);
    setShowCouponModal(true);
  };

  const handleEditCoupon = (coupon) => {
    setCouponForm({
      code: coupon.code,
      discountType: coupon.discountType,
      discountValue: coupon.discountValue,
      minOrderAmount: coupon.minOrderAmount || 0,
      startDate: coupon.startDate ? coupon.startDate.split('T')[0] : '',
      endDate: coupon.endDate ? coupon.endDate.split('T')[0] : '',
      enabled: coupon.enabled
    });
    setEditingCoupon(coupon);
    setShowCouponModal(true);
  };

  const handleSaveCoupon = async () => {
    // Validation
    if (!couponForm.code.trim()) {
      setMessage({ text: 'Coupon code is required', type: 'error' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      return;
    }

    if (!couponForm.discountValue || couponForm.discountValue <= 0) {
      setMessage({ text: 'Discount value must be greater than 0', type: 'error' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
      return;
    }

    try {
      const couponData = {
        code: couponForm.code.trim(),
        discountType: couponForm.discountType,
        discountValue: parseFloat(couponForm.discountValue),
        minOrderAmount: parseFloat(couponForm.minOrderAmount) || 0,
        startDate: couponForm.startDate ? new Date(couponForm.startDate).toISOString() : null,
        endDate: couponForm.endDate ? new Date(couponForm.endDate).toISOString() : null,
        enabled: couponForm.enabled
      };

      if (editingCoupon) {
        await axios.put(`${API_BASE_URL}/api/coupons/${editingCoupon.id}`, couponData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setMessage({ text: 'Coupon updated successfully!', type: 'success' });
      } else {
        await axios.post(`${API_BASE_URL}/api/coupons`, couponData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setMessage({ text: 'Coupon created successfully!', type: 'success' });
      }

      fetchCoupons();
      setShowCouponModal(false);
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      console.error('Coupon save error:', error);
      setMessage({ 
        text: error.response?.data?.detail || error.message || 'Failed to save coupon', 
        type: 'error' 
      });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    }
  };

  const handleToggleCoupon = async (coupon) => {
    try {
      const updatedData = {
        ...coupon,
        enabled: !coupon.enabled
      };
      
      await axios.put(`${API_BASE_URL}/api/coupons/${coupon.id}`, updatedData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setMessage({ 
        text: `Coupon ${!coupon.enabled ? 'enabled' : 'disabled'} successfully!`, 
        type: 'success' 
      });
      fetchCoupons();
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ 
        text: error.response?.data?.detail || 'Failed to toggle coupon status', 
        type: 'error' 
      });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/coupons/${couponId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setMessage({ text: 'Coupon deleted successfully!', type: 'success' });
      fetchCoupons();
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      setMessage({ 
        text: error.response?.data?.detail || 'Failed to delete coupon', 
        type: 'error' 
      });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    }
  };

  // Reset all orders
  const handleResetOrders = async () => {
    setResetting(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/orders/cleanup-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage({ text: 'All orders have been deleted successfully', type: 'success' });
      setShowResetModal(false);
      
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      console.error('Error resetting orders:', error);
      setMessage({ text: 'Failed to reset orders', type: 'error' });
    } finally {
      setResetting(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/api/store-settings`, settings, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      showMessage('Settings saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving settings:', error);
      showMessage('Failed to save settings. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  const handleStateChange = (stateCode) => {
    const selectedState = usStates.find(s => s.code === stateCode);
    setSettings({
      ...settings,
      tax: {
        ...settings.tax,
        selectedState: stateCode,
        taxRate: selectedState ? selectedState.taxRate : 0
      }
    });
  };

  const handleTaxTypeChange = (type) => {
    if (type === 'custom') {
      setSettings({
        ...settings,
        tax: {
          ...settings.tax,
          taxType: 'custom',
          taxRate: settings.tax.customTaxRate || 0
        }
      });
    } else {
      const selectedState = usStates.find(s => s.code === settings.tax.selectedState);
      setSettings({
        ...settings,
        tax: {
          ...settings.tax,
          taxType: 'state',
          taxRate: selectedState ? selectedState.taxRate : 0
        }
      });
    }
  };

  const handleCustomTaxChange = (value) => {
    const customRate = parseFloat(value) || 0;
    setSettings({
      ...settings,
      tax: {
        ...settings.tax,
        customTaxRate: customRate,
        taxRate: customRate
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-white border-b border-gray-200">
            <div className="p-4">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900">
                  Store Settings
                </h2>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-4 sm:p-6">
            {/* Message Display */}
            {message.text && (
              <div className={`mb-6 rounded-md p-4 ${
                message.type === 'success' 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {message.type === 'error' ? (
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    ) : (
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    )}
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm font-medium ${
                      message.type === 'error' ? 'text-red-800' : 'text-green-800'
                    }`}>
                      {message.text}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Tab Navigation */}
            <div className="bg-white rounded-lg shadow-sm mb-4 sm:mb-6 border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === tab.id
                          ? 'border-[rgb(41,173,211)] text-[rgb(41,173,211)]'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <tab.icon className="h-4 w-4" />
                      <span>{tab.label}</span>
                      {settings[tab.id]?.enabled && (
                        <span className="text-green-500">✓</span>
                      )}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {/* Payments Tab */}
                {activeTab === 'payments' && (
                  <div className="space-y-6">
                    {/* Enable Toggle */}
                    <div className="flex items-center justify-between py-4 border-b border-gray-200">
                      <div>
                        <label className="text-sm font-medium text-gray-900">
                          Enable Payments
                        </label>
                        <p className="text-sm text-gray-500">
                          Allow customers to pay with credit cards at checkout
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.payments.enabled}
                          onChange={(e) => setSettings({
                            ...settings,
                            payments: { ...settings.payments, enabled: e.target.checked }
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgba(41,173,211,0.3)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                      </label>
                    </div>

                    {/* Payment Method Selection */}
                    <div className="py-4">
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Select Payment Method
                      </label>
                      <p className="text-sm text-gray-500 mb-3">
                        Choose an active payment method from Payment Settings
                      </p>
                      <select
                        value={settings.payments.selectedPaymentMethod}
                        onChange={(e) => setSettings({
                          ...settings,
                          payments: { ...settings.payments, selectedPaymentMethod: e.target.value }
                        })}
                        className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                        disabled={!settings.payments.enabled}
                      >
                        <option value="">Select a payment gateway</option>
                        {paymentMethods.map((pm) => (
                          <option key={pm.gateway} value={pm.gateway}>
                            {pm.gateway.charAt(0).toUpperCase() + pm.gateway.slice(1)} {pm.environment === 'live' ? '(Live)' : '(Test)'}
                          </option>
                        ))}
                      </select>
                      {paymentMethods.length === 0 && (
                        <p className="text-sm text-amber-600 mt-2">
                          No active payment gateways found. Please enable and verify a payment gateway in Payment Settings first.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Tax Tab */}
                {activeTab === 'tax' && (
                  <div className="space-y-6">
                    {/* Enable Toggle */}
                    <div className="flex items-center justify-between py-4 border-b border-gray-200">
                      <div>
                        <label className="text-sm font-medium text-gray-900">
                          Enable Tax
                        </label>
                        <p className="text-sm text-gray-500">
                          Apply sales tax to orders at checkout
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.tax.enabled}
                          onChange={(e) => setSettings({
                            ...settings,
                            tax: { ...settings.tax, enabled: e.target.checked }
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgba(41,173,211,0.3)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                      </label>
                    </div>

                    {/* Tax Type Selection */}
                    <div className="py-4 border-b border-gray-200">
                      <label className="block text-sm font-medium text-gray-900 mb-4">
                        Tax Configuration
                      </label>
                      <div className="space-y-3">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="taxType"
                            checked={settings.tax.taxType === 'state'}
                            onChange={() => handleTaxTypeChange('state')}
                            className="h-4 w-4 text-[rgb(41,173,211)]"
                            disabled={!settings.tax.enabled}
                          />
                          <span className="ml-2 text-sm text-gray-700">Use State Tax Rate</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="taxType"
                            checked={settings.tax.taxType === 'custom'}
                            onChange={() => handleTaxTypeChange('custom')}
                            className="h-4 w-4 text-[rgb(41,173,211)]"
                            disabled={!settings.tax.enabled}
                          />
                          <span className="ml-2 text-sm text-gray-700">Use Custom Tax Rate</span>
                        </label>
                      </div>
                    </div>

                    {/* State Selection - Only show if State Tax is selected */}
                    {settings.tax.taxType === 'state' && (
                      <div className="py-4 border-b border-gray-200">
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Select State
                        </label>
                        <p className="text-sm text-gray-500 mb-3">
                          Choose your business state for tax calculation
                        </p>
                        <select
                          value={settings.tax.selectedState}
                          onChange={(e) => handleStateChange(e.target.value)}
                          className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                          disabled={!settings.tax.enabled}
                        >
                          <option value="">Select a state</option>
                          {usStates.map((state) => (
                            <option key={state.code} value={state.code}>
                              {state.name} ({state.taxRate}%)
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Custom Tax Rate - Only show if Custom Tax is selected */}
                    {settings.tax.taxType === 'custom' && (
                      <div className="py-4 border-b border-gray-200">
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Custom Tax Rate (%)
                        </label>
                        <p className="text-sm text-gray-500 mb-3">
                          Enter your custom tax rate percentage
                        </p>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          placeholder="0.00"
                          value={settings.tax.customTaxRate}
                          onChange={(e) => handleCustomTaxChange(e.target.value)}
                          className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent text-sm"
                          disabled={!settings.tax.enabled}
                        />
                      </div>
                    )}

                    {/* Tax Rate Display */}
                    {settings.tax.enabled && settings.tax.taxRate > 0 && (
                      <div className="py-4">
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Current Tax Rate
                        </label>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <p className="text-2xl font-bold text-gray-900">
                            {settings.tax.taxRate}%
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            {settings.tax.taxType === 'state' 
                              ? usStates.find(s => s.code === settings.tax.selectedState)?.name 
                              : 'Custom Tax Rate'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Coupons Tab */}
                {activeTab === 'coupons' && (
                  <div className="space-y-6">
                    {/* Enable Coupons Toggle */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div>
                        <label className="text-sm font-medium text-gray-900">Enable Coupons</label>
                        <p className="text-xs text-gray-500 mt-1">Allow customers to use coupon codes at checkout</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.coupons.enabled}
                          onChange={(e) => setSettings({
                            ...settings,
                            coupons: { ...settings.coupons, enabled: e.target.checked }
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                      </label>
                    </div>

                    {/* Add Coupon Button */}
                    <div className="flex justify-end">
                      <button
                        onClick={handleAddCoupon}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,153,191)]"
                      >
                        <Tag className="h-4 w-4" />
                        Add Coupon
                      </button>
                    </div>

                    {/* Coupons List */}
                    {coupons.length === 0 ? (
                      <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                        <Tag className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                        <p className="text-sm text-gray-500">No coupons created yet</p>
                        <button
                          onClick={handleAddCoupon}
                          className="mt-4 text-sm text-[rgb(41,173,211)] hover:text-[rgb(31,153,191)] font-medium"
                        >
                          Create your first coupon
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {coupons.map((coupon) => (
                          <div key={coupon.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-sm font-semibold text-gray-900 font-mono bg-gray-100 px-2.5 py-1 rounded border border-gray-300">
                                    {coupon.code}
                                  </span>
                                  {coupon.enabled ? (
                                    <span className="px-2.5 py-1 text-xs font-medium text-green-700 bg-green-100 rounded border border-green-300">
                                      Active
                                    </span>
                                  ) : (
                                    <span className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded border border-gray-400">
                                      Disabled
                                    </span>
                                  )}
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4 mt-3">
                                  <div>
                                    <p className="text-xs text-gray-500">Discount</p>
                                    <p className="text-sm font-medium text-gray-900">
                                      {coupon.discountType === 'percentage' 
                                        ? `${coupon.discountValue}% off` 
                                        : `$${coupon.discountValue} off`}
                                    </p>
                                  </div>
                                  
                                  {coupon.minOrderAmount > 0 && (
                                    <div>
                                      <p className="text-xs text-gray-500">Minimum Order</p>
                                      <p className="text-sm font-medium text-gray-900">${coupon.minOrderAmount}</p>
                                    </div>
                                  )}
                                  
                                  {(coupon.startDate || coupon.endDate) && (
                                    <div className="col-span-2">
                                      <p className="text-xs text-gray-500">Valid Period</p>
                                      <p className="text-sm font-medium text-gray-900">
                                        {coupon.startDate && new Date(coupon.startDate).toLocaleDateString()} 
                                        {coupon.startDate && coupon.endDate && ' - '}
                                        {coupon.endDate && new Date(coupon.endDate).toLocaleDateString()}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2 ml-4">
                                {/* Toggle Enable/Disable */}
                                <div className="flex items-center">
                                  <label className="relative inline-flex items-center cursor-pointer" title={coupon.enabled ? 'Disable coupon' : 'Enable coupon'}>
                                    <input
                                      type="checkbox"
                                      checked={coupon.enabled}
                                      onChange={() => handleToggleCoupon(coupon)}
                                      className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                                  </label>
                                </div>
                                
                                {/* Edit Button */}
                                <button
                                  onClick={() => handleEditCoupon(coupon)}
                                  className="p-2 text-gray-600 hover:text-[rgb(41,173,211)] hover:bg-gray-100 rounded-lg transition-colors"
                                  title="Edit coupon"
                                >
                                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                
                                {/* Delete Button */}
                                <button
                                  onClick={() => handleDeleteCoupon(coupon.id)}
                                  className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete coupon"
                                >
                                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Coupon Modal */}
                    {showCouponModal && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] flex flex-col overflow-hidden">
                          {/* Sticky Header */}
                          <div className="bg-[rgb(41,173,211)] px-6 py-4 flex items-center justify-between flex-shrink-0">
                            <h3 className="text-lg font-semibold text-white">
                              {editingCoupon ? 'Edit Coupon' : 'Add New Coupon'}
                            </h3>
                            <button
                              onClick={() => setShowCouponModal(false)}
                              className="text-white hover:text-gray-200 transition-colors"
                            >
                              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                          
                          {/* Scrollable Content */}
                          <div className="flex-1 overflow-y-auto p-6 space-y-4">
                            {/* Coupon Code */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Coupon Code *
                              </label>
                              <input
                                type="text"
                                value={couponForm.code}
                                onChange={(e) => setCouponForm({...couponForm, code: e.target.value.toUpperCase()})}
                                placeholder="SAVE10"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent font-mono"
                              />
                            </div>

                            {/* Discount Type */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Discount Type *
                              </label>
                              <div className="grid grid-cols-2 gap-2">
                                <button
                                  type="button"
                                  onClick={() => setCouponForm({...couponForm, discountType: 'percentage'})}
                                  className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                                    couponForm.discountType === 'percentage'
                                      ? 'border-[rgb(41,173,211)] bg-blue-50 text-[rgb(41,173,211)]'
                                      : 'border-gray-300 text-gray-700 hover:border-gray-400'
                                  }`}
                                >
                                  Percentage (%)
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setCouponForm({...couponForm, discountType: 'amount'})}
                                  className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                                    couponForm.discountType === 'amount'
                                      ? 'border-[rgb(41,173,211)] bg-blue-50 text-[rgb(41,173,211)]'
                                      : 'border-gray-300 text-gray-700 hover:border-gray-400'
                                  }`}
                                >
                                  Fixed Amount ($)
                                </button>
                              </div>
                            </div>

                            {/* Discount Value */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Discount Value *
                              </label>
                              <div className="relative">
                                <input
                                  type="number"
                                  value={couponForm.discountValue}
                                  onChange={(e) => setCouponForm({...couponForm, discountValue: parseFloat(e.target.value) || 0})}
                                  min="0"
                                  step={couponForm.discountType === 'percentage' ? '1' : '0.01'}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
                                />
                                <span className="absolute right-3 top-2 text-gray-500">
                                  {couponForm.discountType === 'percentage' ? '%' : '$'}
                                </span>
                              </div>
                            </div>

                            {/* Minimum Order Amount */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Minimum Order Amount (Optional)
                              </label>
                              <input
                                type="number"
                                value={couponForm.minOrderAmount}
                                onChange={(e) => setCouponForm({...couponForm, minOrderAmount: parseFloat(e.target.value) || 0})}
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
                              />
                            </div>

                            {/* Start Date */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Start Date (Optional)
                              </label>
                              <input
                                type="date"
                                value={couponForm.startDate}
                                onChange={(e) => setCouponForm({...couponForm, startDate: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
                              />
                            </div>

                            {/* End Date */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                End Date (Optional)
                              </label>
                              <input
                                type="date"
                                value={couponForm.endDate}
                                onChange={(e) => setCouponForm({...couponForm, endDate: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
                              />
                            </div>

                            {/* Enabled Toggle */}
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <label className="text-sm font-medium text-gray-700">Enable Coupon</label>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={couponForm.enabled}
                                  onChange={(e) => setCouponForm({...couponForm, enabled: e.target.checked})}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(41,173,211)]"></div>
                              </label>
                            </div>
                          </div>

                          {/* Sticky Footer */}
                          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex gap-3 flex-shrink-0">
                            <button
                              onClick={() => setShowCouponModal(false)}
                              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleSaveCoupon}
                              disabled={!couponForm.code || !couponForm.discountValue}
                              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,153,191)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {editingCoupon ? 'Update' : 'Create'} Coupon
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Order Settings Tab */}
                {activeTab === 'orders' && (
                  <div className="space-y-6">
                    {/* Public Access Toggle */}
                    <div className="bg-white p-6 rounded-lg border border-gray-200">
                      <h3 className="text-base font-semibold text-gray-900 mb-4">Online Orders Page Access</h3>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <label className="text-sm font-medium text-gray-900 block mb-1">
                            Public Access
                          </label>
                          <p className="text-sm text-gray-600">
                            When enabled, anyone can access the Online Orders page (/order-online). 
                            When disabled, only logged-in users can place orders.
                          </p>
                        </div>
                        <button
                          onClick={() => setSettings({
                            ...settings,
                            orders: { 
                              ...(settings.orders || {}), 
                              publicAccess: !(settings?.orders?.publicAccess ?? true)
                            }
                          })}
                          className={`ml-4 relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                            settings?.orders?.publicAccess !== false ? 'bg-theme-blue' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              settings?.orders?.publicAccess !== false ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    </div>

                    {/* Order Prefix & Start Number */}
                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                      <h3 className="text-base font-semibold text-gray-900 mb-4">Order Number Configuration</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Configure how order numbers are generated. All new orders will use this format.
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Order Prefix */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Order Prefix
                          </label>
                          <input
                            type="text"
                            value={settings?.orders?.orderPrefix || 'ORD'}
                            onChange={(e) => setSettings({
                              ...settings,
                              orders: { ...(settings.orders || {}), orderPrefix: e.target.value.toUpperCase() }
                            })}
                            placeholder="ORD"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
                          />
                          <p className="text-xs text-gray-500 mt-1">Example: ORD, ORDER, INV</p>
                        </div>

                        {/* Order Start Number */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Starting Number
                          </label>
                          <input
                            type="number"
                            value={settings?.orders?.orderStartNumber || 1000}
                            onChange={(e) => setSettings({
                              ...settings,
                              orders: { ...(settings.orders || {}), orderStartNumber: parseInt(e.target.value) || 1000 }
                            })}
                            min="1"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[rgb(41,173,211)] focus:border-transparent"
                          />
                          <p className="text-xs text-gray-500 mt-1">Orders will start from this number</p>
                        </div>
                      </div>

                      {/* Preview */}
                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">Next Order Number:</span>{' '}
                          <span className="font-mono text-theme-blue">
                            {settings?.orders?.orderPrefix || 'ORD'}-{settings?.orders?.orderStartNumber || 1000}
                          </span>
                        </p>
                      </div>
                    </div>

                    {/* Reset All Orders */}
                    <div className="bg-red-50 p-6 rounded-lg border border-red-200">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <Trash2 className="h-6 w-6 text-red-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-base font-semibold text-gray-900 mb-2">Reset All Orders</h3>
                          <p className="text-sm text-gray-600 mb-4">
                            Permanently delete all orders from the database. This action cannot be undone.
                            Use this for testing or to start fresh.
                          </p>
                          <button
                            onClick={() => setShowResetModal(true)}
                            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                          >
                            <Trash2 className="h-4 w-4" />
                            Reset All Orders
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Save Button */}
                <div className="flex justify-end pt-6 border-t border-gray-200 mt-6">
                  <button
                    onClick={saveSettings}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-[rgb(41,173,211)] rounded-lg hover:bg-[rgb(31,153,191)] disabled:opacity-50"
                  >
                    <Save className="h-4 w-4" />
                    {saving ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reset Orders Confirmation Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Reset All Orders</h3>
              <button
                onClick={() => setShowResetModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 mb-2">
                    Are you absolutely sure?
                  </p>
                  <p className="text-sm text-gray-600">
                    This will permanently delete <strong>ALL orders</strong> from the database. 
                    This action cannot be undone and all order history will be lost forever.
                  </p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-xs text-red-800">
                  ⚠️ <strong>Warning:</strong> This is a destructive action. Make sure you have backed up 
                  any important order data before proceeding.
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex items-center justify-end gap-3">
              <button
                onClick={() => setShowResetModal(false)}
                disabled={resetting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleResetOrders}
                disabled={resetting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {resetting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Yes, Delete All Orders
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreSettings;
